import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { X, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';

interface Announcement {
  id: string;
  title: string | null;
  message: string;
  is_active: boolean;
  expires_at: string | null;
}

export function AnnouncementWidget() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDismissedIds();
    loadAnnouncements();
  }, []);

  // Re-check visibility when dismissedIds or announcements change
  useEffect(() => {
    if (announcements.length > 0) {
      const hasVisibleAnnouncements = announcements.some(
        (ann) => !dismissedIds.has(ann.id)
      );
      setIsVisible(hasVisibleAnnouncements);
    }
  }, [dismissedIds, announcements]);

  const loadDismissedIds = () => {
    try {
      const stored = sessionStorage.getItem('dismissed-announcements');
      if (stored) {
        setDismissedIds(new Set(JSON.parse(stored)));
      }
    } catch (e) {
      logger.error('Error loading dismissed announcements:', e);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('id, title, message, is_active, expires_at')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter out expired announcements
      const now = new Date();
      const activeAnnouncements = (data || []).filter((announcement) => {
        if (!announcement.expires_at) return true;
        return new Date(announcement.expires_at) > now;
      });

      setAnnouncements(activeAnnouncements);
    } catch (error) {
      logger.error('Error loading announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (id: string) => {
    const newDismissed = new Set(dismissedIds);
    newDismissed.add(id);
    setDismissedIds(newDismissed);
    
    try {
      sessionStorage.setItem('dismissed-announcements', JSON.stringify(Array.from(newDismissed)));
    } catch (e) {
      logger.error('Error saving dismissed announcements:', e);
    }

    // Check if there are any remaining visible announcements
    const hasVisibleAnnouncements = announcements.some(
      (ann) => ann.id !== id && !newDismissed.has(ann.id)
    );
    setIsVisible(hasVisibleAnnouncements);
  };

  const handleDismissAll = () => {
    const allIds = new Set(announcements.map((ann) => ann.id));
    setDismissedIds(allIds);
    
    try {
      sessionStorage.setItem('dismissed-announcements', JSON.stringify(Array.from(allIds)));
    } catch (e) {
      logger.error('Error saving dismissed announcements:', e);
    }
    
    setIsVisible(false);
  };

  // Filter out dismissed announcements
  const visibleAnnouncements = announcements.filter((ann) => !dismissedIds.has(ann.id));

  if (loading || !isVisible || visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-5 fade-in duration-500">
      <Card className="shadow-2xl border-0 overflow-hidden bg-white dark:bg-gray-900">
        <CardContent className="p-0">
          <div className="relative">
            {/* Modern gradient header */}
            <div className="relative flex items-center justify-between p-5 bg-gradient-to-br from-purple-600 via-purple-500 to-indigo-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm shadow-lg">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-base tracking-tight">Announcement</h3>
                  {visibleAnnouncements.length > 1 && (
                    <p className="text-xs text-white/80 font-medium mt-0.5">
                      {visibleAnnouncements.length} new messages
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-white/20 rounded-lg transition-colors"
                onClick={handleDismissAll}
              >
                <X className="h-4 w-4 text-white" />
              </Button>
              {/* Decorative gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
            </div>

            {/* Announcements list with modern styling */}
            <div className="max-h-96 overflow-y-auto bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
              {visibleAnnouncements.map((announcement, index) => (
                <div
                  key={announcement.id}
                  className={cn(
                    'p-5 border-b border-gray-100 dark:border-gray-700 last:border-b-0',
                    'transition-all hover:bg-gray-50/50 dark:hover:bg-gray-800/50',
                    index > 0 && 'bg-white/50 dark:bg-gray-800/30'
                  )}
                >
                  {announcement.title && (
                    <div className="flex items-start gap-2 mb-3">
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500 mt-2 flex-shrink-0" />
                      <h4 className="font-bold text-base text-gray-900 dark:text-white leading-tight">
                        {announcement.title}
                      </h4>
                    </div>
                  )}
                  <div className={cn(
                    'text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed',
                    announcement.title ? 'ml-3.5' : ''
                  )}>
                    {announcement.message}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-4 h-8 px-3 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-all"
                    onClick={() => handleDismiss(announcement.id)}
                  >
                    Dismiss
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
